import apiCaller from 'services/Api/ApiCaller';
import { getValidAccessToken } from 'services/Api/refreshHelper';
import { API_URL } from 'settings';

import {
  AssessmentAttachResponse,
  AssessmentContent,
  AssessmentDefinitionDocument,
  AssessmentResultDetail,
  AssessmentResultsResponse,
  AssessmentScenario,
  AssessmentSessionLink,
  AssessmentValidation,
  DefinitionRowKind,
  ImportPreview,
  JsonObject,
} from './types';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const flattenError = (value: unknown, path = ''): string[] => {
  if (Array.isArray(value)) {
    if (value.every(item => item == null || typeof item !== 'object')) {
      const text = value.map(String).join(' ');
      return [path ? `${path}: ${text}` : text];
    }
    return value.flatMap((item, index) =>
      flattenError(item, path ? `${path}.${index}` : String(index)),
    );
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([key, nested]) => flattenError(nested, path ? `${path}.${key}` : key),
    );
  }
  return [path ? `${path}: ${String(value)}` : String(value)];
};

export const assessmentAdminError = (value: unknown): string => {
  const message = flattenError(value).filter(Boolean).join(' ');
  return message || 'The assessment admin request failed.';
};

const request = async <Result, Payload extends object = JsonObject>(
  path: string,
  method: Method = 'GET',
  data?: Payload,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(assessmentAdminError(response.result));
  }
  return response.result as Result;
};

const scenarioPath = (code: string, suffix = '') =>
  `/assessment/admin/scenarios/${encodeURIComponent(code)}${suffix}`;

const scopedDefinitionRequest = async <Result>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  scenario: string,
  payload?: JsonObject,
): Promise<Result> => {
  const token = await getValidAccessToken();
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (token) headers.append('Authorization', `Bearer ${token}`);
  const response = await fetch(
    `${API_URL}${path}/?scenario=${encodeURIComponent(scenario)}`,
    {
      method,
      headers,
      body: method === 'DELETE' ? undefined : JSON.stringify(payload || {}),
    },
  );
  let result: unknown = null;
  try {
    result = await response.json();
  } catch (_error) {
    // DELETE returns the existing contract's empty 204 response.
  }
  if (!response.ok) throw new Error(assessmentAdminError(result));
  return result as Result;
};

export const listAssessmentScenarios = (filters?: {
  q?: string;
  status?: string;
}) =>
  request<AssessmentScenario[], { q?: string; status?: string }>(
    '/assessment/admin/scenarios',
    'GET',
    filters,
  );

export const createAssessmentScenario = (payload: {
  code: string;
  name: string;
  model_version: string;
  params?: JsonObject;
}) =>
  request<AssessmentScenario, typeof payload>(
    '/assessment/admin/scenarios',
    'POST',
    payload,
  );

export const updateAssessmentScenario = (
  code: string,
  payload: Pick<AssessmentScenario, 'name' | 'model_version' | 'params'>,
) =>
  request<AssessmentScenario, typeof payload>(
    scenarioPath(code),
    'PATCH',
    payload,
  );

export const deleteAssessmentScenario = (code: string) =>
  request<null>(scenarioPath(code), 'DELETE');

export const fetchAssessmentContent = (code: string) =>
  request<AssessmentContent>(scenarioPath(code, '/content'));

export const validateAssessmentScenario = (code: string, sessionId?: number) =>
  request<AssessmentValidation, { session_id?: number }>(
    scenarioPath(code, '/validate_definition'),
    'GET',
    sessionId ? { session_id: sessionId } : undefined,
  );

export const cloneAssessmentScenario = (
  sourceCode: string,
  payload: { code: string; name: string; model_version?: string },
) =>
  request<AssessmentScenario, typeof payload>(
    scenarioPath(sourceCode, '/clone'),
    'POST',
    payload,
  );

export const activateAssessmentScenario = (code: string) =>
  request<AssessmentScenario>(scenarioPath(code, '/activate'), 'POST', {});

export const listAssessmentLinks = (code: string) =>
  request<AssessmentSessionLink[]>(scenarioPath(code, '/linked_sessions'));

export const listAssessmentQueue = (filters?: {
  scenario?: string;
  status?: string;
}) =>
  request<AssessmentSessionLink[], { scenario?: string; status?: string }>(
    '/assessment/admin/scenarios/queue',
    'GET',
    filters,
  );

export const createDefinitionRow = <Result>(
  kind: DefinitionRowKind,
  scenario: string,
  payload: JsonObject,
) =>
  scopedDefinitionRequest<Result>(
    `/assessment/admin/${kind}`,
    'POST',
    scenario,
    payload,
  );

export const updateDefinitionRow = <Result>(
  kind: DefinitionRowKind,
  id: number,
  scenario: string,
  payload: JsonObject,
) =>
  scopedDefinitionRequest<Result>(
    `/assessment/admin/${kind}/${id}`,
    'PATCH',
    scenario,
    payload,
  );

export const deleteDefinitionRow = (
  kind: DefinitionRowKind,
  id: number,
  scenario: string,
) =>
  scopedDefinitionRequest<null>(
    `/assessment/admin/${kind}/${id}`,
    'DELETE',
    scenario,
  );

export const attachAssessmentToSession = (
  sessionId: number,
  scenario: string,
) =>
  request<AssessmentAttachResponse, { scenario: string }>(
    `/assessment_sessions/${sessionId}/attach`,
    'POST',
    { scenario },
  );

export const fetchAssessmentResults = (sessionId: number) =>
  request<AssessmentResultsResponse>(
    `/assessment_sessions/${sessionId}/results`,
  );

export const fetchAssessmentResultDetail = (
  sessionId: number,
  player: number,
) =>
  request<AssessmentResultDetail, { player: number }>(
    `/assessment_sessions/${sessionId}/result_detail`,
    'GET',
    { player },
  );

export const recomputeAssessment = (sessionId: number) =>
  request<{ detail: string }>(
    `/assessment_sessions/${sessionId}/recompute`,
    'POST',
    {},
  );

export const previewAssessmentImport = (payload: JsonObject) =>
  request<ImportPreview, JsonObject>(
    '/assessment/admin/scenarios/import_preview',
    'POST',
    payload,
  );

export const commitAssessmentImport = (payload: JsonObject) =>
  request<JsonObject, JsonObject>(
    '/assessment/admin/scenarios/import_data',
    'POST',
    payload,
  );

const authenticatedDownload = async (path: string, fallbackName: string) => {
  const token = await getValidAccessToken();
  const headers = new Headers();
  if (token) headers.append('Authorization', `Bearer ${token}`);
  const response = await fetch(`${API_URL}${path}`, { headers, method: 'GET' });
  if (!response.ok) {
    const body = await response.text();
    let detail: unknown = body;
    try {
      detail = JSON.parse(body);
    } catch (_error) {
      // Keep non-JSON proxy or server errors as plain text.
    }
    throw new Error(assessmentAdminError(detail));
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const downloadAssessmentDefinition = (
  code: string,
  format: 'json' | 'csv',
) =>
  authenticatedDownload(
    `${scenarioPath(code, '/export_definition')}/?file_format=${format}`,
    `${code}-assessment.${format === 'csv' ? 'zip' : 'json'}`,
  );

export const downloadAssessmentResults = (
  sessionId: number,
  format: 'json' | 'csv',
) =>
  authenticatedDownload(
    `/assessment/admin/scenarios/result_export/?session_id=${sessionId}&file_format=${format}`,
    `session-${sessionId}-assessment-results.${format}`,
  );

export const fetchAssessmentDefinition = (code: string) =>
  request<AssessmentDefinitionDocument>(
    scenarioPath(code, '/export_definition'),
  );
